// Account-management helpers: data export (App Store + GDPR-friendly) and
// account deletion. Deletion runs via a Postgres function (security definer)
// declared in supabase/schema.sql so we don't need to ship the service-role
// key to a Pages Function.

import { supabase } from "./supabase";
import { clearAllReadThroughCache } from "./cache";

export interface ExportedData {
  exported_at: string;
  app: "calorie-counter";
  version: 1;
  user: {
    id: string;
    email: string | null;
    created_at: string | null;
  };
  meals: unknown[];
  favorites: unknown[];
  goals: unknown | null;
  user_prefs: unknown | null;
  weight_logs: unknown[];
  water_logs: unknown[];
  recipes: unknown[];
}

/**
 * Pull every row the user owns. Each query is RLS-gated to auth.uid(), so
 * there's no risk of cross-user reads — but we still don't trust the user-id
 * column on the way out.
 */
export async function exportUserData(): Promise<ExportedData> {
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  // Run reads in parallel. Treat per-table failures as empty rather than
  // failing the whole export — a missing table shouldn't lose the rest.
  const [meals, favorites, goals, prefs, weights, waters, recipes] = await Promise.all([
    safeSelect("meals"),
    safeSelect("favorites"),
    safeSingle("goals"),
    safeSingle("user_prefs"),
    safeSelect("weight_logs"),
    safeSelect("water_logs"),
    safeSelect("recipes")
  ]);

  return {
    exported_at: new Date().toISOString(),
    app: "calorie-counter",
    version: 1,
    user: {
      id: user.id,
      email: user.email ?? null,
      created_at: user.created_at ?? null
    },
    meals,
    favorites,
    goals,
    user_prefs: prefs,
    weight_logs: weights,
    water_logs: waters,
    recipes
  };
}

async function safeSelect(table: string): Promise<unknown[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from(table).select("*");
  if (error) {
    console.warn(`exportUserData: ${table} failed`, error);
    return [];
  }
  return data ?? [];
}

async function safeSingle(table: string): Promise<unknown | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.from(table).select("*").maybeSingle();
  if (error) {
    console.warn(`exportUserData: ${table} failed`, error);
    return null;
  }
  return data ?? null;
}

/** Trigger a download of the user's data as a JSON file. */
export function downloadAsJson(data: ExportedData): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `calorie-counter-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Delete every trace of the current user. Wraps the `delete_my_account` SQL
 * function which removes the auth.users row; all owned data CASCADEs from
 * `on delete cascade` foreign keys. The user's photos in Storage are NOT
 * cascaded — Supabase Storage doesn't tie to auth.users — so we also try to
 * clean up the bucket folder (best-effort: deletion still completes if this
 * fails).
 */
export async function deleteMyAccount(): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");

  // Best-effort photo cleanup. Storage path = "<user_id>/<file>".
  try {
    const { data: files } = await supabase.storage.from("meal-photos").list(user.id);
    if (files && files.length) {
      const paths = files.map((f) => `${user.id}/${f.name}`);
      await supabase.storage.from("meal-photos").remove(paths);
    }
  } catch (err) {
    console.warn("Could not clean up meal-photos storage", err);
  }

  // Delete the auth.users row via SQL function. CASCADEs handle every owned table.
  const { error: rpcError } = await supabase.rpc("delete_my_account");
  if (rpcError) throw rpcError;

  // Drop the now-invalid local session + every cached row.
  clearAllReadThroughCache();
  await supabase.auth.signOut();
}
